import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import * as actions from 'js/actions';
import { ROUTE_LOGIN } from 'js/constants/pathnames';
import { getCurrentUser } from 'js/reducers/session';
import { useApp } from 'new/contexts/AppContextProvider';
import useQueryParams from 'new/hooks/useQueryParams';
import { EmbeddedModeLoader } from 'new/elements/EmbeddedModeLoader';
import { useRestrictedTiers } from 'new/hooks/useRestrictedTiers';
import { isPathRestrictedByTier } from 'new/routes/routes.util';
export default function PrivateRoute({
  path,
  component: Component,
  exact = false,
  defaultProps = {},
}) {
  const dispatch = useDispatch();
  const { setHideSideBar } = useApp();
  const { embedded } = useQueryParams();
  const { hasExpressTierLimitedAccess, hasSelfSignupTierLimitedAccess } = useRestrictedTiers();
  const currentPath = useSelector((state) => state.routes.currentPath);
  const currentUser = useSelector(getCurrentUser);
  const userLoggedIn = !!currentUser;

  const isEmbeddedMode = embedded === 'true';

  useEffect(() => {
    const hide = Component?.screenConfig?.hideSideBar || isEmbeddedMode;
    setHideSideBar(hide || false);
    // Cleanup: reset hideSideBar when component unmounts or changes
    return () => {
      setHideSideBar(false);
    };
  }, [Component, isEmbeddedMode, setHideSideBar]);

  const { setCurrentPath } = bindActionCreators(
    { setCurrentPath: actions.setCurrentPath },
    dispatch,
  );

  function _setNewPath(newPath) {
    setTimeout(() => {
      setCurrentPath(newPath);
    }, 0);
  }

  return (
    <Route
      path={path}
      exact={exact}
      render={(props) => {
        if (props.match && props.match.isExact && currentPath !== props.location.pathname) {
          _setNewPath(`${props.location.pathname}${props.location.search}`);
        }

        if (
          hasExpressTierLimitedAccess &&
          isPathRestrictedByTier(props.location.pathname, 'express_tiers')
        ) {
          return (
            <Redirect
              to={{
                pathname: '/404',
                state: { from: props.location },
              }}
            />
          );
        }

        if (
          hasSelfSignupTierLimitedAccess &&
          isPathRestrictedByTier(props.location.pathname, 'self_signup_tiers')
        ) {
          return (
            <Redirect
              to={{
                pathname: '/404',
                state: { from: props.location },
              }}
            />
          );
        }

        if (userLoggedIn) {
          return <Component {...props} {...defaultProps} />;
        } else if (isEmbeddedMode) {
          // In embedded mode, show loading while waiting for external token authentication
          // Don't redirect to login - wait for postMessage authentication
          return <EmbeddedModeLoader />;
        } else {
          // Normal redirect to login for non-embedded mode
          return (
            <Redirect
              to={{
                pathname: ROUTE_LOGIN,
                state: { from: props.location },
              }}
            />
          );
        }
      }}
    />
  );
}
